import { useCallback, useEffect, useRef, type KeyboardEvent, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalFocus {
  panelRef: RefObject<HTMLDivElement | null>;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
}

function focusablesIn(panel: HTMLElement): HTMLElement[] {
  return [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)];
}

function restoreFocus(opener: Element | null): void {
  if (opener instanceof HTMLElement && document.contains(opener)) {
    opener.focus();
    return;
  }

  const landmark = document.querySelector('main');
  if (!landmark) return;

  landmark.tabIndex = -1;
  landmark.focus();
}

export function useModalFocus(isOpen: boolean, onClose: () => void): ModalFocus {
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    openerRef.current = document.activeElement;
    panelRef.current?.focus();

    return () => {
      restoreFocus(openerRef.current);
    };
  }, [isOpen]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (!panel) return;

      const focusables = focusablesIn(panel);
      if (focusables.length === 0) return;

      const [first, ...rest] = focusables;
      const last = rest.at(-1) ?? first;

      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  return { panelRef, onKeyDown };
}
